import { TenantViewModel } from '@ail/customers-api'
import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import IdIsCopied from '../../../helpers/IdIsCopied'
import { useGetTenantsQuery, useDeleteTenantMutation, useGetTenantsStatsQuery } from '../../../redux/api/customerApi'
import { useAuth } from '../../../hooks/useAuth'
import Alert from '../../common/Alert/Alert'
import Confirm from '../../common/Confirm/Confirm'
import './GroupsAdminPanel.scss'
import { TenantStatistics } from './tenantStatistics/TenantStatistics'
import { TenantCard } from './tenantCard/TenantCard'
import { Arr } from '../../../helpers/Arr'
import { Loader } from '../../common/Loader/Loader'

interface TypesFilter {
  customer: boolean
  supplier: boolean
  insurance: boolean
}

export function GroupsAdminPanelFunctional() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const { data: tenants = [], isLoading } = useGetTenantsQuery()
  const [deleteTenant] = useDeleteTenantMutation()

  const [filterGroups, setFilterGroups] = useState('')
  const [filterGroupsTags, setFilterGroupsTags] = useState('')
  const [typesFilter, setTypesFilter] = useState<TypesFilter>({
    customer: false,
    supplier: false,
    insurance: false,
  })
  const [idCopied, setIdCopied] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [tenantToDelete, setTenantToDelete] = useState<TenantViewModel>()
  const [deleteAlertState, setDeleteAlertState] = useState({
    visible: false,
    message: '',
    title: '',
  })

  //RTK invalidation tags after user editing is not available, requres force refetch to update automatically
  //After userAdminPanel refactor, RTK tag invalidation would be possible
  const { data } = useGetTenantsStatsQuery(undefined, { refetchOnMountOrArgChange: true })
  const counters = useMemo(() => {
    const count = { customersCount: 0, activeUsers: 0, inactiveUsers: 0 }
    if (!data) return count

    return data.reduce((acc, val) => {
      acc.customersCount += val.customersCount
      acc.activeUsers += val.activeUsers
      acc.inactiveUsers += val.inactiveUsers
      return acc
    }, count)
  }, [data])
  const statsMap = useMemo(
    () =>
      Arr.toHashMap(
        data ?? [],
        (stat) => stat.tenantId,
        (stat) => stat
      ),
    [data]
  )
  // Filter tenants by type
  const typeFilteredTenants = useMemo(() => {
    if (!typesFilter.customer && !typesFilter.supplier && !typesFilter.insurance) {
      return tenants
    }

    return tenants.filter(
      (tenant) =>
        (typesFilter.customer && tenant.isCompany) ||
        (typesFilter.supplier && tenant.isSupplier) ||
        (typesFilter.insurance && tenant.isInsurance)
    )
  }, [tenants, typesFilter])

  // Filter tenants by search strings
  const filteredTenants = useMemo(() => {
    return typeFilteredTenants.filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(filterGroups.toLowerCase()) &&
        tenant.tags.join(', ').toLowerCase().includes(filterGroupsTags.toLowerCase())
    )
  }, [typeFilteredTenants, filterGroups, filterGroupsTags])

  const applyTypeFilter = useCallback((type: 'customer' | 'supplier' | 'insurance') => {
    setTypesFilter((prev) => {
      switch (type) {
        case 'customer':
          return { ...prev, customer: !prev.customer }
        case 'insurance':
          return { ...prev, insurance: !prev.insurance }
        case 'supplier':
          return { ...prev, supplier: !prev.supplier }
        default:
          return prev
      }
    })
  }, [])

  const copyTenantID = useCallback((id: string) => {
    navigator.clipboard.writeText(id)
    setIdCopied(true)
    setTimeout(() => {
      setIdCopied(false)
    }, 4000)
  }, [])

  const handleDeleteTenant = useCallback((tenant: TenantViewModel) => {
    setTenantToDelete(tenant)
    setShowDeleteConfirm(true)
  }, [])

  const handleConfirmClose = useCallback(
    async (confirm: boolean) => {
      if (confirm && tenantToDelete?.id) {
        try {
          await deleteTenant(tenantToDelete.id).unwrap()
          setDeleteAlertState({
            visible: true,
            message: t('ITEM_DELETED'),
            title: t('SUCESS'),
          })
        } catch {
          setDeleteAlertState({
            visible: true,
            message: t('ITEM_DELETE_FAILED'),
            title: t('CONFLICT'),
          })
        }
      }
      setShowDeleteConfirm(false)
      setTenantToDelete(undefined)
    },
    [tenantToDelete, deleteTenant, t]
  )

  const toggleAlert = useCallback(() => {
    setDeleteAlertState((prev) => ({ ...prev, visible: !prev.visible }))
  }, [])

  return (
    <>
      <Loader blocking={isLoading} style={{ overflow: 'hidden' }}>
        <ul className="breadcrumbs">
          <li>
            <NavLink to="/UserManagement">
              <span>{t('ADMIN_PANEL')}</span>
            </NavLink>
          </li>
          <li>
            <span>{t('TENANTS')}</span>
          </li>
          <li>
            <span></span>
          </li>
        </ul>
        <div className="row adminArea GroupsAdminPanel">
          <div className="col-md-3 col-6 searchArea">
            <div className="box boxArea searchBar">
              <input
                name={`fieldnameveryunique${new Date().getTime()}`}
                autoComplete="none"
                list="autocompleteOff"
                value={filterGroups}
                onChange={(e) => setFilterGroups(e.target.value)}
                type="search"
                placeholder={t('FIND_GROUP_BY_KEYWORD')}
              />
              {!!filterGroups && <i className="icon-clear" onClick={() => setFilterGroups('')} />}
              <i className="icon-search"></i>
            </div>
          </div>
          <div className="col-md-3 col-6 searchArea">
            <div className="box boxArea searchBar">
              <input
                name={`fieldnameveryunique${new Date().getTime()}`}
                autoComplete="none"
                list="autocompleteOff"
                value={filterGroupsTags}
                onChange={(e) => setFilterGroupsTags(e.target.value)}
                type="search"
                placeholder={t('FIND_GROUP_BY_TAGS')}
              />
              {!!filterGroupsTags && <i className="icon-clear" onClick={() => setFilterGroupsTags('')} />}
              <i className="icon-search"></i>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="box groupArea types">
              <div>{t('FILTER_BY_TYPE')}</div>
              <div className="typesFilter">
                <div onClick={() => applyTypeFilter('customer')}>
                  {t('CUSTOMER')} <input type="checkBox" checked={typesFilter.customer} readOnly />
                </div>
                <div onClick={() => applyTypeFilter('supplier')}>
                  {t('SUPPLIER')} <input type="checkBox" checked={typesFilter.supplier} readOnly />
                </div>
                <div onClick={() => applyTypeFilter('insurance')}>
                  {t('INSURANCE')} <input type="checkBox" checked={typesFilter.insurance} readOnly />
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <TenantStatistics counters={counters} />
          </div>
          <div className="itemList">
            {user?.isAdmin && (
              <div className="col-6 col-md-3">
                <NavLink
                  className="box groupArea newBox"
                  to="/UserManagement/groups/group/edit/new"
                  style={{ height: '216px' }}
                >
                  <i className="icon-add"></i>
                  {t('NEW_TENANT')}
                </NavLink>
              </div>
            )}
            {filteredTenants.map((tenant) => (
              <TenantCard
                key={tenant.id}
                tenant={tenant}
                onCopy={copyTenantID}
                onDelete={handleDeleteTenant}
                stats={statsMap[tenant.id]}
              />
            ))}
          </div>
          {idCopied && <IdIsCopied idCopied={idCopied} />}
        </div>
      </Loader>
      <Confirm
        visible={showDeleteConfirm}
        onClose={handleConfirmClose}
        title={t('PLEASE_CONFIRM')}
        message={`${t('DO_YOU_WANT_TO_DELETE')} ${tenantToDelete?.name}?`}
      />
      <Alert
        onClose={toggleAlert}
        visible={deleteAlertState.visible}
        title={deleteAlertState.title}
        message={deleteAlertState.message}
      />
    </>
  )
}
